import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { supabase } from "./supabaseClient";
import { useAuth } from "./auth/AuthProvider";
import { track, updateSuperProperties } from "./analytics";

type SubStatus = "loading" | "active" | "trialing" | "past_due" | "inactive" | "canceled";

interface SubscriptionState {
  status: SubStatus;
  priceId: string | null;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
}

const SubscriptionContext = createContext<SubscriptionState>({
  status: "loading",
  priceId: null,
  cancelAtPeriodEnd: false,
  currentPeriodEnd: null,
});

export function useSubscription() {
  return useContext(SubscriptionContext);
}

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const [state, setState] = useState<SubscriptionState>({
    status: "loading",
    priceId: null,
    cancelAtPeriodEnd: false,
    currentPeriodEnd: null,
  });
  const fetchedForUser = useRef<string | null>(null);
  // Last status we observed for this user, so we can detect a mid-session
  // transition into a paid state (funnel completion) without firing on the
  // initial load of an already-subscribed user.
  const prevStatus = useRef<SubStatus | null>(null);

  useEffect(() => {
    if (auth.status === "loading") return; // Wait for auth to resolve first

    if (auth.status !== "signed_in") {
      setState({ status: "inactive", priceId: null, cancelAtPeriodEnd: false, currentPeriodEnd: null });
      fetchedForUser.current = null;
      return;
    }

    // Don't re-fetch if we already have a result for this user
    if (fetchedForUser.current === auth.user.id) return;

    // Reset to loading so BurrowLoader shows instead of flashing PricingPage
    setState(prev => prev.status !== "loading" ? { ...prev, status: "loading" } : prev);

    (async () => {
      const { data, error } = await supabase
        .from("subscriptions")
        .select("status, price_id, cancel_at_period_end, current_period_end")
        .eq("user_id", auth.user.id)
        .maybeSingle();

      fetchedForUser.current = auth.user.id;

      if (error || !data) {
        prevStatus.current = "inactive";
        setState({ status: "inactive", priceId: null, cancelAtPeriodEnd: false, currentPeriodEnd: null });
        return;
      }

      const nextStatus = (data.status as SubStatus) ?? "inactive";
      const becamePaid =
        (nextStatus === "active" || nextStatus === "trialing") &&
        prevStatus.current != null &&
        prevStatus.current !== "active" &&
        prevStatus.current !== "trialing";
      if (becamePaid) {
        track("subscription_activated", { status: nextStatus, price_id: data.price_id });
      }
      // Keep every subsequent event segmentable by the user's current plan.
      updateSuperProperties({
        plan: nextStatus === "active" || nextStatus === "trialing" ? "premium" : "free",
        subscription_status: nextStatus,
      });
      prevStatus.current = nextStatus;

      setState({
        status: nextStatus,
        priceId: data.price_id,
        cancelAtPeriodEnd: data.cancel_at_period_end ?? false,
        currentPeriodEnd: data.current_period_end,
      });
    })();
  }, [auth.status, auth.user]);

  return (
    <SubscriptionContext.Provider value={state}>
      {children}
    </SubscriptionContext.Provider>
  );
}
